/* eslint-disable no-magic-numbers */
/* eslint-disable react-hooks/immutability */
/* eslint-disable react/jsx-no-bind */
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Button from 'Component/Button';
import {
    SIZE_NONE, SIZE_SMALL,
    TEXT_SIZE_SMALL,
    TYPE_OPTIONS,
    TYPE_TEXT, WIDTH_HUG} from 'Component/Button/Button.config';
import Html from 'Component/Html';
import Link from 'Component/Link';
import NoticeBanner from 'Component/NoticeBanner';
import { BANNER_INFO } from 'Component/NoticeBanner/NoticeBanner.config';
import PRODUCT_TYPE from 'Component/Product/Product.config';
import ProductOption from 'Component/ProductOption/ProductOption.component';
import { SWATCH_SMALL } from 'Component/ProductOption/ProductOption.config';
import { COLOR_DISPLAY_GROUP, SIZE_DISPLAY_GROUP } 
from 'Component/ProductOptions/ProductOptions.config';
import RulerIconComponent from 'Component/RulerIcon';
import ScrollSwiper from 'Component/ScrollSwiper';
import ScrollSwiperSlide from 'Component/ScrollSwiperSlide';
import { PRODUCT_CUSTOM_PLACEMENT } from 'Route/ProductPage/ProductPage.config';
import { AttributesType } from 'Type/ProductList.type';
import { lazyComponentLoader } from 'Util/lazyComponentLoader';
import {
    getProductInStock
} from 'Util/Product/Extract';
import { STOCK_TYPE } from 'Util/Product/Stock.config';

import { S_KEY } from '../Icon/Icon.config'
const SizeGuideActionComponent =  lazyComponentLoader(() => import(
    /* webpackMode: "lazy", webpackChunkName: "size-guide-action" */
    'Component/SizeGuideAction'
), 2);

/** @namespace Bodypwa/Component/ProductOptions/Component/ProductOptions */
export const ProductOptions = (props) => {
    const {
        configurable_options = {},
        handleOptionClick,
        selectedOptionValue = null,
        selectedOptionsValues = {},
        getSelectOption = () => {},
        swatchClass = '',
        isSwiper = false,
        url,
        isPlp = true,
        variants = [],
        optionClassName = '',
        attributeClassName = '',
        togglePlacement = () => { },
        discontinued,
        sizeSwatch = SWATCH_SMALL,
        isPlacementEnabled = false,
        product = {},
        activePlacementTrigger = null,
        setIsPlacementEnabled = () => { },
        isB2bCustomer = false,
        registerOptionRef = () => {},
        hasPreselections = false,
        activeProduct = {}
    } = props;

    const { placement_suggestions } = product;

    const isPlacementRecommendationEnabled = useSelector(
        (state) => state.ConfigReducer.is_placement_enabled
    );
    

    const pdpVariantOptionsGroups = useSelector(
        (state) => state.ConfigReducer.pdp_variant_options_groups || ''
    ).split(',').map((group) => group.trim());

    const optionsToRender = useMemo(() => {
        if (isPlp) {
            return Object.values(configurable_options)
                .filter((i) => i.attribute_display_group === COLOR_DISPLAY_GROUP)
                .slice(0, 1);
        }

        return Object.values(configurable_options);
    }, [configurable_options, isPlp]);

    const hasNonColorSelections = useMemo(() => (
        optionsToRender
            .filter(({ attribute_display_group }) => attribute_display_group === SIZE_DISPLAY_GROUP)
            .every(({ attribute_code: code }) => !!selectedOptionsValues[code])
    ), [optionsToRender, selectedOptionsValues]);

    const shouldHideChangeAction = useMemo(() => (
        optionsToRender
            .filter(({ attribute_display_group }) => attribute_display_group === SIZE_DISPLAY_GROUP)
            .every(({ attribute_values }) => (attribute_values?.length || 0) <= 1)
    ), [optionsToRender]);

    const getSizeAttributeCodes = () => optionsToRender
        .filter(({ attribute_display_group }) => attribute_display_group === SIZE_DISPLAY_GROUP)
        .map(({ attribute_code }) => attribute_code);

    const getIsConfigurableAttributeAvailable = ({ attribute_code, attribute_value }) => {
        if (!variants?.length) {
            return true;
        }

        const parameterPairs = Object.entries(selectedOptionsValues);
        const isAttributeSelected = Boolean(selectedOptionsValues[attribute_code]);

        const relevantSelections = isAttributeSelected
            ? parameterPairs.filter(([key]) => key !== attribute_code)
            : parameterPairs;

        return variants.some(({ stock_status, attributes }) => {
            const { attribute_value: foundValue } = attributes[attribute_code] || {};

            return (
                stock_status === STOCK_TYPE.IN_STOCK
                && foundValue === attribute_value
                && relevantSelections.every(
                    ([k, v]) => attributes[k]?.attribute_value === v
                )
            );
        });
    };

    const isAnySelectedSizeOOS = () => {
        const sizeCodes = getSizeAttributeCodes();

        return sizeCodes.some((code) => {
            const selectedValue = selectedOptionsValues[code];

            if (!selectedValue) {
                return true;
            }

            return !getIsConfigurableAttributeAvailable({
                attribute_code: code,
                attribute_value: selectedValue
            });
        });
    };

    const isSizeOOS = isAnySelectedSizeOOS();
    const inStock = getProductInStock(product);
    const placementDisabled = !inStock && product.type_id === PRODUCT_TYPE.configurable;

    const getAttributeLabel = (attributeCode, selectedValue) => {
        if (!selectedValue) {
            return null;
        }

        const attribute = optionsToRender.find(
            (opt) => opt.attribute_code === attributeCode
        );

        const option = attribute?.attribute_options?.[selectedValue];
        return option?.label || null;
    };

    const renderLabel = (attribute_label, attributeCode, showChangeAction) => {
        if (isPlp) {
            return null;
        }

        const selectedValue = selectedOptionsValues[attributeCode];
        const label = getAttributeLabel(attributeCode, selectedValue);

        const isAvailable = getIsConfigurableAttributeAvailable({
            attribute_code: attributeCode,
            attribute_value: selectedValue
        });

        const OOSLabel = activePlacementTrigger !== PRODUCT_CUSTOM_PLACEMENT
            && !isB2bCustomer && isPlacementRecommendationEnabled && inStock
            ? __('Choose available %s', attribute_label) : __('%s is out of stock', label || attribute_label);

        const labelClass = label ? 'text-bmText-default' : 'text-bmStatus-alert';

        return (
            <div className="flex flex-row justify-between mb-2">
                <div className="flex flex-row">
                    <div className="text-p14 font-bold">
                        { attribute_label }
                        <span className="mr-1">:</span>
                    </div>
                    { (!isAvailable && !discontinued && selectedValue) ? (
                        <p className="text-bmStatus-alert text-p14 font-normal mb-0">
                            { OOSLabel }
                        </p>
                    ) : inStock && (
                        <p className={ `${labelClass} text-p14 font-normal mb-0` }>
                            { label || __(!discontinued ? 'Choose %s' : '', attribute_label) }
                        </p>
                    ) }
                </div>
                { (showChangeAction && !shouldHideChangeAction && isPlacementEnabled && !isSizeOOS) && (
                    <Button
                        textSize={ TEXT_SIZE_SMALL }
                        onClick={ () => setIsPlacementEnabled(false) }
                        text={ __('Change') }
                        type={ TYPE_TEXT }
                        width={ WIDTH_HUG }
                        size={ SIZE_NONE }
                    />
                ) }
                { (attributeCode === firstSizeAttrCode
                        && (!isPlacementRecommendationEnabled || !placement_suggestions)) 
                        && <SizeGuideActionComponent /> }
            </div>
        );
    };
    
    const otherSelectedOptions = () => {

        if ( activePlacementTrigger === PRODUCT_CUSTOM_PLACEMENT) {
            return;
        }
        togglePlacement(PRODUCT_CUSTOM_PLACEMENT) 
    };

    const renderPreselection = () => {
        if (!product?.placement_suggestions || isB2bCustomer || !isPlacementRecommendationEnabled) {
            return null;
        }

        const { type_id, placement_suggestions: { rules = [] } = {} } = product;
        if (type_id !== PRODUCT_TYPE.configurable || !inStock) {
            return null;
        }

        if (!rules.length) {
            return null;
        }

        const placementLabel = activePlacementTrigger ? __('Other')
         : __(!discontinued && inStock ? 'Choose Piercing' : '');
        const label = rules
            .find(({ trigger_value_index }) => String(trigger_value_index) === activePlacementTrigger)?.trigger_label;

        return (
            <div className="mb-6">
                <p className="text-p14 mb-2 flex justify-between">
                    <span>
                        <span className="mr-1 font-bold">
                            { __('Piercing:') }
                        </span>
                        <span className={ label ? '' : 'text-bmText-default' }>
                            { label || placementLabel }
                        </span>
                    </span>
                    <SizeGuideActionComponent />
                </p>
                <ScrollSwiper isControls={ false }>
                    {  rules.map((rule) => (
                        <ScrollSwiperSlide
                            widthClass="!w-auto mr-2"
                            isActive={ String(rule.trigger_value_index) === String(activePlacementTrigger)
                                && !placementDisabled }
                        >
                            <Button
                                textSize={ TEXT_SIZE_SMALL }
                                type={ TYPE_OPTIONS }
                                onClick={ () => togglePlacement(rule.trigger_value_index) }
                                size={ SIZE_SMALL }
                                isDisabled={ placementDisabled }
                                isActive={ String(rule.trigger_value_index)
                                    === String(activePlacementTrigger) && !placementDisabled }
                                key={ rule.trigger_value_index }
                                width={ WIDTH_HUG }
                                text={ rule.trigger_label }
                            >
                                { placementDisabled && (
                                    <div className="pointer-events-none absolute left-1/2 top-1/2 w-[42px] h-px
                                        bg-bmText-default transform -translate-x-1/2 -translate-y-1/2 -rotate-45"
                                    />
                                ) }
                            </Button>
                        </ScrollSwiperSlide>
                    )) }
                    <ScrollSwiperSlide widthClass="!w-auto"  
                        isActive={ activePlacementTrigger === PRODUCT_CUSTOM_PLACEMENT }>
                        <Button
                            isActive={ activePlacementTrigger === PRODUCT_CUSTOM_PLACEMENT }
                            textSize={ TEXT_SIZE_SMALL }
                            type={ TYPE_OPTIONS }
                            onClick={ otherSelectedOptions }
                            size={ SIZE_SMALL }
                            isDisabled={ placementDisabled }
                            isActive={ activePlacementTrigger === PRODUCT_CUSTOM_PLACEMENT }
                            key="custom.choice"
                            width={ WIDTH_HUG }
                            text={ __('Other') }
                        >
                            { placementDisabled && (
                                <div className="pointer-events-none absolute left-1/2 top-1/2 w-[42px] h-px
                                    bg-bmText-default transform -translate-x-1/2 -translate-y-1/2 -rotate-45"
                                />
                            ) }
                        </Button>
                    </ScrollSwiperSlide>
                </ScrollSwiper>
            </div>
        );
    };

    const buildProps = (configurableOptions, value, plpSelectedValue, pdpSelectedValue) => ({
        swatchClass,
        sizeSwatch,
        attribute: { ...configurableOptions, attribute_value: value },
        isSelected: plpSelectedValue === value || pdpSelectedValue === value,
        isAvailable: getIsConfigurableAttributeAvailable({
            ...configurableOptions,
            attribute_value: value
        }),
        onClick: handleOptionClick
    });

    const renderSlider = (values, renderExtra, itemProps) => (
        <ScrollSwiper isAutoScroll isHideNavigationButton swiperWrapperClass="gap-1 items-center">
            { values.map((value) => (
                <ScrollSwiperSlide key={ value } widthClass="!w-auto lg:!mt-3 !mt-2 mr-0">
                    <ProductOption { ...itemProps(value) } />
                </ScrollSwiperSlide>
            )) }

            { !!renderExtra && (
                <ScrollSwiperSlide widthClass="!w-auto mt-1">
                    { renderExtra() }
                </ScrollSwiperSlide>
            ) }
        </ScrollSwiper>
    );

    const renderGrid = (values, renderExtra, itemProps) => (
        <div className="flex gap-2 flex-wrap">
            { values.map((value) => (
                <ProductOption key={ value } { ...itemProps(value) } />
            )) }
            { !!renderExtra && renderExtra() }
        </div>
    );

    const renderValues = isPlp ? renderSlider : renderGrid;

    const getFilteredEnrichedAttributes = (existingOptions, displayGroups) => {
        const { attributes } = product;
        const { attributes: variantAttributes } = variants?.[0] || {};
        const finalAttributes = activeProduct.sku === product.sku ? { ...variantAttributes, ...attributes }
                    : { ...attributes, ...activeProduct.attributes };
        const enrichedAttributes = Object.entries(finalAttributes).reduce((acc, [key, value]) => {
            const fallbackValues = value.attribute_values
                || value.attribute_options_order
                || Object.keys(value.attribute_options || []);

            if (fallbackValues.length) {
                acc[key] = {
                    ...value,
                    attribute_values: fallbackValues,
                    display_option_value: true
                };
            }

            return acc;
        }, {});

        const existingAttributeIds = new Set(
            existingOptions.map((option) => option.attribute_id)
        );

        return Object.values(enrichedAttributes).filter((attr) => {
            const hasDisplayGroup = Boolean(displayGroups.includes(attr.attribute_display_group));
            const isDuplicate = existingAttributeIds.has(attr.attribute_id);

            return hasDisplayGroup && !isDuplicate;
        });
    };

    optionsToRender.push(...(isPlp ? [] : getFilteredEnrichedAttributes(
        optionsToRender,
        pdpVariantOptionsGroups
    )));


    const toPosition = (position) => {
        const parsed = Number(position);
        return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
    };

    optionsToRender.sort((a, b) => toPosition(a.attribute_display_position) - toPosition(b.attribute_display_position));

    const firstSizeAttrCode = !isPlp
        ? optionsToRender.find((opt) => opt.attribute_display_group === SIZE_DISPLAY_GROUP)?.attribute_code
        : null;

    const renderConfigurableAttributes = () => optionsToRender.map((configurableOptions) => {
        const {
            attribute_code, attribute_values, attribute_label,
            attribute_display_group, display_option_value, attribute_options
        } = configurableOptions;
        
        if (display_option_value) {
            return (
                <>
                    { attribute_code === firstSizeAttrCode && renderPreselection() }
                    <div className="flex flex-row justify-between mb-3 first:mb-6">
                        <p className="text-p14 !mb-0">
                            <span className="font-bold">
                                { attribute_label }
                                :
                                { ' ' }
                            </span>
                            { attribute_options?.[attribute_values]?.label || attribute_values }
                        </p>
                        { (attribute_code === firstSizeAttrCode && !shouldHideChangeAction
                        && isPlacementEnabled && !isSizeOOS) && (
                            <Button
                                textSize={ TEXT_SIZE_SMALL }
                                onClick={ () => setIsPlacementEnabled(false) }
                                text={ __('Change') }
                                type={ TYPE_TEXT }
                                width={ WIDTH_HUG }
                                size={ SIZE_NONE }
                            />
                        ) }
                    </div>
                </>
            );
        }
        
        if (!attribute_values?.length) {
            return null;
        }

        const lastFilterMatchedValue = Array.from(attribute_values)
            .reverse()
            .find((value) => getSelectOption(attribute_code, value));

        const visibleValues = isSwiper
            ? attribute_values.slice(0, 6)
            : attribute_values;

        const remainingSwatches = attribute_values.length - visibleValues.length;

        const plpSelectedValue = selectedOptionValue ?? lastFilterMatchedValue;
        const pdpSelectedValue = isPlp ? '' : (selectedOptionsValues[attribute_code] ?? lastFilterMatchedValue);

        const itemProps = (value) => buildProps(configurableOptions, value, plpSelectedValue, pdpSelectedValue);

        const renderExtra = () => remainingSwatches > 0 && (
            <Link className="underline text-p12 !text-bmText-muted" to={ url }>
                +
                { remainingSwatches }
            </Link>
        );

        const showValues = isPlacementEnabled
            || (attribute_values.length <= 1 && activePlacementTrigger !== PRODUCT_CUSTOM_PLACEMENT);

        const showOptions = (activePlacementTrigger && isPlacementRecommendationEnabled) 
         ||  attribute_display_group !== SIZE_DISPLAY_GROUP
         || !isPlacementRecommendationEnabled || isB2bCustomer || !product?.placement_suggestions || !inStock;
        
        const isHideValue = showValues && attribute_display_group === SIZE_DISPLAY_GROUP
                        && !isSizeOOS && hasNonColorSelections;


        return (
            <>
                { attribute_code === firstSizeAttrCode && renderPreselection() }
                { showOptions && <div
                    key={ attribute_code }
                    ref={ (el) => registerOptionRef(attribute_code, el) }
                    className={ isHideValue ? attributeClassName : optionClassName }
                >
                    { renderLabel(
                        attribute_label,
                        attribute_code,
                        attribute_code === firstSizeAttrCode
                    ) }
                    { isHideValue ? null
                        : renderValues(visibleValues, renderExtra, itemProps) }
                </div> }
            </>
        );
    });

    const renderPreslectionNotice = () => {
        if (!isPlacementEnabled || !activePlacementTrigger || !hasPreselections || isSizeOOS) {
            return null;
        }

        const { placement_suggestions: { rules = [] } = {} } = product;

        const rule = rules.find(
            (r) => String(r.trigger_value_index) === String(activePlacementTrigger)
        );

        const content = __('We have selected the most common size for %s piercings. ',
                `<strong class="lowercase">${rule?.trigger_label || ''}</strong>`);

        return (
            <div className="preselection-notice mb-6 mt-3">
                <NoticeBanner
                    icon={ RulerIconComponent }
                    type={ BANNER_INFO }
                    iconSize={ S_KEY }
                >
                    <div className="text-p12 [&_strong]:text-p12">
                        <Html content={ content } />
                    </div>
                </NoticeBanner>
            </div>
        );
    };

    return (
        <div>
            { renderConfigurableAttributes() }
            { renderPreslectionNotice() }
        </div>
    );
};

ProductOptions.propTypes = {
    configurable_options: AttributesType.isRequired,
    handleOptionClick: PropTypes.func.isRequired,
    selectedOptionValue: PropTypes.string,
    selectedOptionsValues: PropTypes.shape({}),
    getSelectOption: PropTypes.func,
    swatchClass: PropTypes.string,
    sizeSwatch: PropTypes.string,
    isSwiper: PropTypes.bool,
    url: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
        search: PropTypes.string
    }).isRequired,
    isPlp: PropTypes.bool,
    variants: PropTypes.arrayOf(PropTypes.shape({})),
    className: PropTypes.string,
    discontinued: PropTypes.bool.isRequired,
    product: PropTypes.shape({
        type_id: PropTypes.string,
        placement_suggestions: PropTypes.shape({
            rules: PropTypes.arrayOf(PropTypes.shape({}))
        }),
        attributes: PropTypes.shape({}),
        sku: PropTypes.string
    }).isRequired,
    activeProduct: PropTypes.shape({
        type_id: PropTypes.string,
        placement_suggestions: PropTypes.shape({
            rules: PropTypes.arrayOf(PropTypes.shape({}))
        }),
        attributes: PropTypes.shape({}),
        sku: PropTypes.string
    }).isRequired,
    togglePlacement: PropTypes.func,
    isPlacementEnabled: PropTypes.bool,
    activePlacementTrigger: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setIsPlacementEnabled: PropTypes.func,
    registerOptionRef: PropTypes.func,
    isB2bCustomer: PropTypes.bool,
    hasPreselections: PropTypes.bool
};

export default ProductOptions;
